import { Component, createRef } from 'react'
import _ from 'lodash'
import { connect } from 'react-redux'
import {
  reduxForm,
  getFormValues,
  getFormMeta,
  getFormSyncErrors,
  clearSubmitErrors,
  untouch,
  change,
  focus
} from 'redux-form'
import reduxFormActions from 'redux-form/lib/actions'
import { REDUX_FORM_STATE_KEY } from './index'
import { SKIP_REQUIRED_FIELD } from '../../../constants/proposalTypes'
import { pruneEmpty, FORM_ERROR_MESSAGE } from '../../../lib/FormHelper'

/**
 * Custom redux-from decorator
 *  enhanced with updateFormSyncErrors to update sync errors
*/
const withFormDecorator = (config) => (Comp) => {
  const formKey = config.form
  const Decorated = reduxForm(config)(Comp)

  class FormDecorator extends Component {
    ref = createRef()

    getReduxFormState = () => {
      return this.props[REDUX_FORM_STATE_KEY] || {}
    }

    triggerFieldChange = (field, value) => {
      const { form } = this.getReduxFormState()
      this.props.dispatch(change(form, field, value))
    }

    triggerFieldFocus = (field) => {
      const { form } = this.getReduxFormState()
      this.props.dispatch(focus(form, field))
    }

    updateFormSyncErrors = (errors) => {
      const { dispatch } = this.props
      const { form } = this.getReduxFormState()
      dispatch(reduxFormActions.updateSyncErrors(form, errors, errors))
    }

    getFieldList = (options) => {
      const { registeredFields } = this.getReduxFormState()
      const list = []
      if (!registeredFields) {
        return list
      }
      let keySeq = _.keys(registeredFields)
      if (options && options.excludeFieldArray) {
        keySeq = keySeq.filter(
          name =>
            name.indexOf('[') === -1 && _.get(registeredFields, `['${name}'].type`) !== 'FieldArray'
        )
      }
      return keySeq.reduce((acc, key) => {
        acc.push(key)
        return acc
      }, list)
    }

    untouchAllFields = () => {
      const { dispatch } = this.props
      const { form } = this.getReduxFormState()
      const fieldList = this.getFieldList({ excludeFieldArray: true })
      _.each(fieldList, field => dispatch(untouch(form, field)))
    }

    clearSubmitErrors = () => {
      const { dispatch } = this.props
      const { form } = this.getReduxFormState()
      this.untouchAllFields()
      dispatch(clearSubmitErrors(form))
    }

    // TODO: this is a attampt to fix go around the submit to validate the form but failed to connect the submitting status
    // ref: https://github.com/erikras/redux-form/blob/master/src/handleSubmit.js
    skipRequireSubmit = (skipRequired) => {
      const reduxFormRef = _.get(this, 'reduxForm.ref')
      const formInstance = _.get(reduxFormRef, 'wrappedInstance')
      const formProps = formInstance.props
      const { validate, values } = reduxFormRef.props
      const shouldError = formInstance.shouldErrorFunction()
      const fieldLevelValidate = formInstance.generateValidator()
      const nextProps = undefined
      const structure = null
      const nextValues = _.merge({}, formProps.values)
      const { startSubmit, stopSubmit, setSubmitFailed, setSubmitSucceeded } = formProps

      startSubmit()

      if (validate || fieldLevelValidate) {
        const initialRender = nextProps === undefined
        const fieldValidatorKeys = Object.keys(formInstance.getValidators())
        const validateParams = {
          values,
          nextProps,
          props: formProps,
          initialRender,
          lastFieldValidatorKeys: formInstance.lastFieldValidatorKeys,
          fieldValidatorKeys,
          structure
        }
        if (shouldError(validateParams)) {
          const propsToValidate = formProps
          _.set(nextValues, SKIP_REQUIRED_FIELD, true)
          const { _error, ...nextSyncErrors } = _.merge(
            validate
              ? validate(nextValues, propsToValidate) || {}
              : {},
            fieldLevelValidate
              ? fieldLevelValidate(
                nextValues,
                propsToValidate
              ) || {}
              : {}
          )
          const prunedSyncErrors = pruneEmpty(nextSyncErrors)
          if (!_.isEmpty(prunedSyncErrors)) {
            const fields = formInstance.getFieldList({ excludeFieldArray: true })
            stopSubmit(prunedSyncErrors)
            setSubmitFailed(...fields)
            throw new Error(FORM_ERROR_MESSAGE)
          }
        }
      }

      stopSubmit()
      setSubmitSucceeded()
      return nextValues
    }

    render () {
      return (
        <Decorated
          ref={this.ref}
          {...this.props}
          triggerFieldChange={this.triggerFieldChange}
          triggerFieldFocus={this.triggerFieldFocus}
          clearSubmitErrors={this.clearSubmitErrors}
          updateFormSyncErrors={this.updateFormSyncErrors}
        />
      )
    }
  }

  return connect(
    (state) => ({
      [REDUX_FORM_STATE_KEY]: {
        form: formKey,
        config,
        registeredFields: _.get(state, `form.${formKey}.registeredFields`),
        values: getFormValues(formKey)(state),
        meta: getFormMeta(formKey)(state),
        syncErrors: getFormSyncErrors(formKey)(state)
      }
    }),
    null,
    null,
    { forwardRef: true }
  )(FormDecorator)
}

export default withFormDecorator
